import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Typography,
  Col,
  Row,
  Modal,
  InputNumber,
  message,
  Input,
  TimePicker,
  Spin,
  Divider,
  Tooltip,
} from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import { InfoCircleOutlined, AudioOutlined } from "@ant-design/icons";
import {
  addIoMonitoringData,
  processAudioIoMonitoringApi,
  updateIoMonitoringData,
} from "../../../../services/nurse.services";
import useQuery from "../../../../hooks/useQuery";
import useCompactMonitoringLayout from "../../../../hooks/useCompactMonitoringLayout";
import useTabletMonitoringLayout from "../../../../hooks/useTabletMonitoringLayout";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../../IpEMR/Doctor/InitialInspection/ClosePopup";
import dayjs from "dayjs";
import AiVoiceRecording from "./AiVoiceRecording";

const format = "hh:mm A";
const getDisabledTime = () => {
  const now = dayjs();

  return {
    disabledHours: () => {
      const hours = [];
      for (let i = now.hour() + 1; i < 24; i++) {
        hours.push(i);
      }
      return hours;
    },

    disabledMinutes: (selectedHour) => {
      if (selectedHour === now.hour()) {
        const minutes = [];
        for (let i = now.minute() + 1; i < 60; i++) {
          minutes.push(i);
        }
        return minutes;
      }
      return [];
    },

    disabledSeconds: () => [],
  };
};

const IoModalForm = ({ open, setOpen, editData, refresh, date }) => {
  const params = useQuery();
  const [loading, setLoading] = useState(false);
  const [hourlyIntake, setHourlyIntake] = useState(0);
  const [hourlyOutput, setHourlyOutput] = useState(0);
  const [startRecord, setStartRecord] = useState(false);
  const [recordedFile, setRecordedFile] = useState(null);
  const [showRecord, setShowRecord] = useState(false);
  const [recordLoading, setRecordLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [showButton, setShowButton] = useState(true);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const isCompact = useCompactMonitoringLayout();
  const isTablet = useTabletMonitoringLayout();

  useEffect(() => {
    const baseData = editData || {}; // existing form data
    const aiData = aiResponse || {}; // new ai data

    // Merge intelligently: keep existing values if AI gives 0
    const mergedValues = { ...baseData };
    Object.keys(aiData).forEach((key) => {
      if (
        aiData[key] !== 0 && // AI really changed value
        aiData[key] !== null &&
        aiData[key] !== "" &&
        aiData[key] !== undefined
      ) {
        mergedValues[key] = aiData[key];
      }
    });
    form.setFieldsValue({
      ...mergedValues,
      process_time: mergedValues?.process_time
        ? dayjs.utc(mergedValues.process_time)
        : null,
    });
    if (editData) {
      form.setFieldsValue({
        ...mergedValues,
        process_time: dayjs?.utc(editData?.process_time),
      });
      setHourlyIntake(
        (mergedValues?.oral_tube_intake || 0) +
          (mergedValues?.flush || 0) +
          (mergedValues?.iv_fluids || 0)
      );
      setHourlyOutput(
        (mergedValues?.uo || 0) +
          (mergedValues?.gi_losses || 0) +
          (mergedValues?.drain || 0)
      );
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        process_time: dayjs(),
      });
      setHourlyIntake(0);
      setHourlyOutput(0);
    }
  }, [editData, aiResponse, open, date]);
  // Cleanup recordings when modal closes
  useEffect(() => {
    if (!open) {
      setStartRecord(false);
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }
      // Reset recording states
      setShowRecord(false);
      setRecordedFile(null);
      setShowButton(true);
    }
  }, [open]);

  // Function to calculate hourly intake and output
  const calculateValues = (changedValues, allValues) => {
    const newHourlyIntake =
      (allValues?.oral_tube_intake || 0) +
      (allValues?.flush || 0) +
      (allValues?.iv_fluids || 0);

    const newHourlyOutput =
      (allValues?.uo || 0) +
      (allValues?.gi_losses || 0) +
      (allValues?.drain || 0);

    setHourlyIntake(newHourlyIntake);
    setHourlyOutput(newHourlyOutput);
  };

  const onValuesChange = (changedValues, allValues) => {
    calculateValues(changedValues, allValues);
  };

  const onFinish = (values) => {
    setLoading(true);
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    // Combine date prop with the selected time from TimePicker
    const selectedTime = values?.process_time
      ? dayjs(values?.process_time).format("HH:mm")
      : null;
    const combinedDateTime = selectedTime
      ? dayjs(date)
          .set("hour", dayjs(values?.process_time).hour())
          .set("minute", dayjs(values?.process_time).minute())
          .format("YYYY-MM-DDTHH:mm:ss")
      : editData?.process_time;

    // Adding computed fields to the values object
    const updatedValues = {
      ...values,
      process_time: combinedDateTime, // Use the combined date and time
      hourly_intake: hourlyIntake || 0,
      hourly_output: hourlyOutput || 0,
    };

    if (editData?.id) {
      updateIoMonitoringData(params?.patient, editData?.id, updatedValues)
        ?.then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          refresh();
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          message?.error(err?.response?.data?.message);
        });
    } else {
      addIoMonitoringData(params?.patient, updatedValues)
        ?.then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          refresh();
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          message?.error(err?.response?.data?.message);
        });
    }
  };
  const onRecordSubmit = async (values) => {
    const combinedDateTime = editData?.action_time
      ? editData?.action_time
      : null;

    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    // Stop recording before processing
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    setRecordLoading(true);
    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);
      fd.append("action_time", JSON.stringify(combinedDateTime));
      const response = await processAudioIoMonitoringApi(fd);
      setAiResponse(response?.data || {});
      setShowRecord(false);
      setShowButton(true);
      message.success("Audio processed successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setRecordLoading(false);
    }
  };

  const handleCancel = () => {
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }
    setOpen(false);
  };

  const formItems = [
    { name: "Oral/Tube", value: "oral_tube_intake" },
    { name: "GI Losses", value: "gi_losses" },
    { name: "Flush", value: "flush" },
    { name: "UO", value: "uo" },
    { name: "IV", value: "iv_fluids" },
    { name: "Drain", value: "drain" },
  ];

  return (
    <div>
      <Modal
        open={open}
        onCancel={isCompact ? handleCancel : undefined}
        closeIcon={isCompact ? undefined : null}
        width={
          isTablet
            ? "min(720px, calc(100vw - 48px))"
            : isCompact
              ? "calc(100vw - 32px)"
              : "min(720px, calc(100vw - 48px))"
        }
        centered
        wrapClassName={isCompact ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
        getContainer={isCompact ? () => document.body : undefined}
        className={[
          "nurse-monitoring-io-modal",
          "nurse-monitoring-vitals-modal",
          isTablet ? "nurse-monitoring-vitals-modal--tablet" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        destroyOnClose={isCompact || undefined}
        maskClosable={false}
        keyboard={false}
        title={
          isCompact ? (
            "IO Monitoring"
          ) : (
            <Row>
              <Col span={12} className="flex-start">
                <Typography
                  style={{
                    marginLeft: "12px",
                    fontWeight: "400",
                    fontSize: "20px",
                  }}
                >
                  Input/Output monitoring
                </Typography>
              </Col>
            </Row>
          )
        }
        footer={
          <>
            <Divider
              className={isCompact ? "nurse-monitoring-modal-footer-divider" : undefined}
              style={{ margin: "16px 0" }}
            />

            <div
              className={
                isCompact
                  ? [
                      "nurse-monitoring-modal-footer",
                      isTablet ? "nurse-monitoring-modal-footer--tablet" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : undefined
              }
              style={{
                display: "flex",
                justifyContent: editData?.id ? "space-between" : "flex-end",
                alignItems: "center",
              }}
            >
              {editData?.id && (
                <Button
                  type="primary"
                  icon={<AudioOutlined />}
                  size={isCompact ? "middle" : "large"}
                  className={isCompact ? "nurse-monitoring-ai-btn" : undefined}
                  onClick={() => {
                    setShowButton(false);
                    setStartRecord(true);
                    setShowRecord(true);
                  }}
                  disabled={!showButton}
                >
                  AI Voice Assistant
                </Button>
              )}

              <div
                className={isCompact ? "nurse-monitoring-modal-actions" : undefined}
                style={{ display: "flex", gap: "16px" }}
              >
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    type="primary"
                    ghost
                    className={`${btnStyles.mdBtn} inv-btn`}
                    onClick={handleCancel}
                    disabled={loading || recordLoading}
                  >
                    Cancel
                  </Button>
                </Form.Item>

                <Form.Item style={{ margin: 0 }}>
                  <Button
                    disabled={recordedFile && !aiResponse}
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                    className={`${btnStyles.mdBtn} inv-btn`}
                    onClick={() => {
                      if (formRef.current) {
                        formRef.current.submit();
                      }
                    }}
                  >
                    Save
                  </Button>
                </Form.Item>
              </div>
            </div>
          </>
        }
      >
        <Divider
          className={isCompact ? "nurse-monitoring-modal-top-divider" : undefined}
          style={{ margin: "5px" }}
        />
        {recordLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Processing..." />
          </div>
        ) : (
          <Form
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={onValuesChange} // Trigger calculation on field change
            form={form}
            ref={formRef}
          >
            <Form.Item
              className={isTablet ? "nurse-monitoring-time-item" : undefined}
              label="Time"
              name="process_time"
              rules={[{ required: true, message: "Please Select time" }]}
            >
              <TimePicker
                inputReadOnly
                format={format}
                needConfirm={isCompact}
                disabledTime={getDisabledTime}
                style={{ width: "100%" }}
                placement="bottomLeft"
                popupClassName={isCompact ? "curengo-mobile-time-picker" : undefined}
                getPopupContainer={isCompact ? () => document.body : undefined}
              />
            </Form.Item>
            <Row gutter={[12, 0]} className="nurse-monitoring-form-grid">
              {formItems?.map((item) => (
                <Col xs={24} sm={12} key={item.value}>
                  <Form.Item
                    label={item.name}
                    name={item.value}
                    rules={[
                      {
                        type: "number",
                        message: "Please enter a valid number",
                      },
                    ]}
                  >
                    <InputNumber addonAfter="ml" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              ))}

              <Col xs={24}>
                <div className={isCompact ? "nurse-monitoring-io-summary" : undefined}>
                  <Row align="middle" gutter={isCompact ? 12 : 24}>
                    <Col xs={24} md={12}>
                      <Typography.Text>
                        Hourly Intake: {hourlyIntake} ml{" "}
                        <Tooltip
                          title={
                            <span style={{ fontSize: "10px" }}>
                              Oral/Tube + Flush + IV fluids
                            </span>
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Typography.Text>
                    </Col>

                    <Col xs={24} md={12}>
                      <Typography.Text>
                        Hourly Output: {hourlyOutput} ml{" "}
                        <Tooltip
                          title={
                            <span style={{ fontSize: "10px" }}>
                              GI Losses + UO + Drain
                            </span>
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Typography.Text>
                    </Col>
                  </Row>
                </div>
                <div style={{ marginTop: isCompact ? 6 : "10px" }}>
                  <Form.Item label="Remarks :" name="additional_info">
                    <Input.TextArea />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        )}

        {showRecord && !recordLoading && (
          <AiVoiceRecording
            setRecordedFile={setRecordedFile}
            startRecord={startRecord}
            setStartRecord={setStartRecord}
            onRecordSubmit={onRecordSubmit}
            stopAllRecordings={stopRecordingRef}
            type="io_monitoring"
          />
        )}
      </Modal>
    </div>
  );
};

export default IoModalForm;
